import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch } from 'universal-scripts'
import {
  addItem,
  closeItemEditor,
  replaceItem
} from 'src/store/slices/dashboard'
import type { AllowedColSpan } from 'src/types/dashboard'
import { S3_IMAGE_BUCKET } from 'src/constants/global'
import Loader from '@components/Loader/Loader'
import useUploadImage from 'src/hooks/useUploadImage'

interface ImageFormProps {
  initial: { title: string; image: string }
  itemId: string | null
  existingWidth?: AllowedColSpan
}

const ALLOWED_FORMATS = new Set(['jpeg', 'png', 'webp', 'gif', 'avif', 'tiff'])
const MAX_FILE_SIZE = 2 * 1024 * 1024

export default function ImageForm({
  initial,
  itemId,
  existingWidth
}: ImageFormProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const uploadImage = useUploadImage()
  const [title, setTitle] = useState(initial.title)
  const [file, setFile] = useState<File | null>(null)
  const [localPreview, setLocalPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const formatsString = Array.from(ALLOWED_FORMATS).join(', ').toUpperCase()

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview)
    }
  }, [localPreview])

  const savedImage = initial.image

  const isValid = (file !== null || savedImage.trim() !== '') && !uploading

  const previewSrc =
    localPreview ??
    (savedImage ? `${S3_IMAGE_BUCKET}/images/${savedImage}.png` : null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    if (!picked) return

    let format = picked.type.split('/')[1]
    if (!format) format = picked.name.split('.').pop()?.toLowerCase() ?? ''
    if (format === 'jpg') format = 'jpeg'
    if (format === 'tif') format = 'tiff'

    if (!ALLOWED_FORMATS.has(format)) {
      setError(
        intl.formatMessage(
          { id: 'error.file-format-not-allowed' },
          { formats: formatsString }
        )
      )
      e.target.value = ''
      return
    }

    if (picked.size > MAX_FILE_SIZE) {
      setError(
        intl.formatMessage({ id: 'error.file-too-large' }, { maxSize: '2 MB' })
      )
      e.target.value = ''
      return
    }

    if (localPreview) {
      URL.revokeObjectURL(localPreview)
    }

    setFile(picked)
    setLocalPreview(URL.createObjectURL(picked))
    setError(null)
  }

  const handleApply = async () => {
    if (!isValid) return

    let image = savedImage
    if (file) {
      setUploading(true)
      setError(null)
      try {
        image = await uploadImage(file)
      } catch {
        setError(intl.formatMessage({ id: 'item-editor.image.error' }))
        setUploading(false)
        return
      }
      setUploading(false)
    }

    const width: AllowedColSpan = existingWidth ?? 3

    if (itemId) {
      dispatch(
        replaceItem({
          itemId,
          item: { itemId, type: 'image', width, title: title.trim(), image }
        })
      )
    } else {
      dispatch(
        addItem({
          itemId: crypto.randomUUID(),
          type: 'image',
          width,
          title: title.trim(),
          image
        })
      )
    }

    dispatch(closeItemEditor())
  }

  const handleCancel = () => dispatch(closeItemEditor())

  return (
    <aside id="item-editor-panel">
      <header className="item-editor-header">
        <h3>
          <FormattedMessage id="item-editor.chart.editor-title" />
        </h3>
        <div className="item-editor-actions">
          <button
            className="highlight-button"
            onClick={() => void handleApply()}
            disabled={!isValid}
          >
            <span>
              <FormattedMessage id="node-editor.button.apply" />
            </span>
          </button>
          <span
            className="icon icon-cross standart-button"
            onClick={handleCancel}
          />
        </div>
      </header>
      <div className="item-editor-body">
        <label>
          <h4>
            <FormattedMessage id="item-editor.label.title" />
          </h4>
          <input
            type="text"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'item-editor.label.title.placeholder'
            })}
          />
        </label>
        <label>
          <h4>
            <FormattedMessage id="item-editor.image.title" />
          </h4>
          <input
            type="file"
            onChange={handleFileChange}
            accept="image/jpeg, image/png, image/webp, image/gif, image/avif, image/tiff, .jpeg, .jpg, .png, .webp, .gif, .avif, .tiff, .tif"
            disabled={uploading}
          />
        </label>
        <p className="requirements-text">
          <FormattedMessage
            id="item-editor.image.text"
            values={{
              formats: formatsString,
              maxSize: '2 MB'
            }}
          />
        </p>

        {uploading ? (
          <Loader />
        ) : (
          previewSrc && (
            <img className="image-preview" src={previewSrc} alt={title} />
          )
        )}
        {error && <p className="error">{error}</p>}
      </div>
    </aside>
  )
}
